import { useState } from "react";
import { getCostHistory, getDashboardSummary, getDauTrend } from "../api/dashboard";
import { Pagination } from "../components/Pagination";
import { useAsync } from "../hooks/useAsync";
import { formatBeijingTime } from "../utils/datetime";

/** v2.30:看板内明细列表的每页条数。 */
const DETAIL_PAGE_SIZE = 10;

/**
 * v2.30:纯前端分页切片。
 *
 * 到期预警/闲置用户/DAU 这三个列表的数据,后端本来就是在
 * `/dashboard/summary`、`/dashboard/dau-trend` 里**一次性全量返回**的,
 * 因此加分页不需要改后端接口、也不需要新增查询参数,在前端切片即可
 * (与用户列表页那种服务端分页是不同场景——那边数据量大且本来就分页查询)。
 *
 * page 可能因为数据变少(如到期预警随时间减少)而越界,这里统一夹到合法
 * 范围再切,避免翻到后面几页后数据刷新出现空白页。
 */
function paginate<T>(items: T[], page: number): { rows: T[]; totalPages: number; safePage: number } {
  const totalPages = Math.max(1, Math.ceil(items.length / DETAIL_PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const start = (safePage - 1) * DETAIL_PAGE_SIZE;
  return { rows: items.slice(start, start + DETAIL_PAGE_SIZE), totalPages, safePage };
}

/**
 * 运营看板,对应产品文档 §8.2。
 *
 * v2.10:原"成本趋势(近 30 天)"图表位置改为"每日活跃用户数"(Active 指
 * subscription_status=ACTIVE)。成本趋势本身没有删除,移到了新增的独立
 * Cost Summary 页面(见 CostSummaryPage.tsx),与"每一个纳管账号的
 * Month-to-date 费用"一起展示。
 */
export function DashboardPage() {
  const summary = useAsync(() => getDashboardSummary(), []);
  const dauTrend = useAsync(() => getDauTrend(30), []);
  // v2.30:"本月预估费用"改为读每日费用快照(取最近 2 个月足够覆盖"本月";
  // 多带一个上月便于月初快照尚未运行时判断是"没跑"还是"真没有")。
  const costHistory = useAsync(() => getCostHistory(2), []);

  // v2.30:三个明细列表的页码(hooks 必须在下面的提前 return 之前声明)
  const [expiryPage, setExpiryPage] = useState(1);
  const [idlePage, setIdlePage] = useState(1);
  const [dauPage, setDauPage] = useState(1);

  if (summary.loading) return <div>加载中...</div>;
  if (summary.error) return <div className="error-banner">{summary.error}</div>;
  if (!summary.data) return null;

  const d = summary.data;
  // §6:激活率按"订阅状态=Active"的数量/占比展示(与 activation_health.activation_rate
  // 语义不同——那个是"激活状态=ACTIVE 的用户 ÷ 未取消订阅的用户",这里按需求
  // 改成更直观的"订阅状态=Active 的数量和占用户总数的百分比"),直接汇总各账号
  // 的 active_count,不需要新增后端接口。分母用 total_users(不含已退订),
  // 与"用户总数"卡片的口径保持一致。
  const activeSubscriptionCount = d.account_summaries.reduce((sum, a) => sum + a.active_count, 0);
  const activeSubscriptionRate = d.total_users > 0 ? (activeSubscriptionCount / d.total_users) * 100 : 0;

  // v2.30:本月费用改为读 kiro_cost_snapshots 每日快照(经 /dashboard/cost-history),
  // 而不是实时调 Cost Explorer。
  //
  // 为什么不直接用 Cost Summary 页那个实时接口:`/dashboard/cost-summary` 每次调用
  // 会对"平台账号 + 每个已启用纳管账号"各发一次 Cost Explorer GetCostAndUsage
  // (当前 7 个纳管账号 = 8 次请求),而 Cost Explorer API 是**按请求计费**的。
  // 看板是首页、打开频率远高于 Cost Summary 页,把它挂上去会带来一笔随纳管账号数
  // 线性增长的持续开销。快照表是 DynamoDB 读取,成本可忽略。
  //
  // 代价(已与用户确认可接受):数字是"上一次每日快照时"的值,与 Cost Summary 页的
  // 实时值存在**日内差异**,不是严格相等。但统计口径本身一致——两者都只统计有数据的
  // 账号(Cost Explorer 查询失败的账号不会写入快照,因此也不计入合计,见
  // cost_summary_service.run_cost_snapshot 的容错说明);且 Cost Explorer 本身有
  // T+1 结算延迟,当天数据无论走哪条路都不完整。
  //
  // 月份键用 UTC 计算,与后端口径保持一致(run_cost_snapshot 用 date.today(),
  // 月份取自 Cost Explorer TimePeriod.Start[:7],均为 UTC)。
  const currentMonthKey = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const currentMonthCost = costHistory.data?.months.find((m) => m.month === currentMonthKey) ?? null;

  const expiry = paginate(d.expiry_warnings_7d, expiryPage);
  const idle = paginate(d.idle_users, idlePage);
  // v2.30:按需求"最近的一天显示在最上面"。后端 dau_repo.list_snapshots 是按
  // 日期升序返回的(DynamoDB sort key 天然升序),这里在前端反转,不动后端接口
  // (该接口只有本页在用,但改后端会连带影响已有测试与数据语义,前端反转成本更低)。
  const dauPointsDesc = [...(dauTrend.data?.data_points ?? [])].reverse();
  const dau = paginate(dauPointsDesc, dauPage);

  return (
    <div>
      <h2 className="section-title">运营看板</h2>

      <div className="card-grid">
        <div className="card">
          <div style={{ fontSize: 13, color: "#64748b" }}>用户总数</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{d.total_users}</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: "#64748b" }}>激活率(订阅状态=Active)</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>
            {activeSubscriptionCount} / {d.total_users}
            <span style={{ fontSize: 16, fontWeight: 400, marginLeft: 6, color: "#64748b" }}>
              ({activeSubscriptionRate.toFixed(1)}%)
            </span>
          </div>
        </div>
        {/* v2.30:此前这里显示的是 metrics_service 按"层级单价 × 用户数"算出的
            估算值,而 Cost Summary 页显示的是 Cost Explorer 的真实 Month-to-date
            费用——两个完全不同的数据源,数字长期对不上,运营容易误判。现改为与
            Cost Summary 同一套费用口径,数据取自每日费用快照(避免每次打开首页都
            触发按请求计费的 Cost Explorer 调用,详见上方 currentMonthCost 处注释)。 */}
        <div className="card">
          <div style={{ fontSize: 13, color: "#64748b" }}>本月预估费用(USD)</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>
            {costHistory.loading
              ? "加载中..."
              : currentMonthCost === null
                ? "暂无数据"
                : `$${currentMonthCost.total_usd.toLocaleString()}`}
          </div>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            来自每日费用快照(仅统计有数据的账号);实时值见 Cost Summary 页
          </div>
          {costHistory.error && (
            <div style={{ fontSize: 12, color: "#b45309" }}>费用快照查询失败: {costHistory.error}</div>
          )}
          {!costHistory.loading && !costHistory.error && currentMonthCost === null && (
            <div style={{ fontSize: 12, color: "#b45309" }}>
              本月({currentMonthKey})尚无快照,每日定时任务运行后显示
            </div>
          )}
          {currentMonthCost && (
            <div style={{ fontSize: 12, color: "#64748b" }}>
              已统计 {currentMonthCost.accounts.length} 个账号
            </div>
          )}
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: "#64748b" }}>退订数量</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{d.unsubscribed_count}</div>
        </div>
      </div>

      <div className="card">
        <div className="section-title">层级分布</div>
        {/* 数量口径:不含已退订(CANCELED)的用户,即 Active + Pending,
            与上方"用户总数"卡片口径一致。额外展示 Active/Pending/退订数
            三个子维度,退订数单独列出但不计入"数量"合计。 */}
        <table>
          <thead>
            <tr>
              <th>层级</th>
              <th>数量</th>
              <th>Active</th>
              <th>Pending</th>
              <th>退订数</th>
            </tr>
          </thead>
          <tbody>
            {d.tier_distribution_overall.map((t) => (
              <tr key={t.tier}>
                <td>{t.tier}</td>
                <td>{t.count}</td>
                <td>{t.active_count}</td>
                <td>{t.pending_count}</td>
                <td>{t.canceled_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="section-title">按账号维度</div>
        {/* §8.2 v2.10:"退订数"口径改为统计 subscription_status=CANCELED,
            与原本就存在的 canceled_count 是同一个数字,因此这里合并为一列,
            不再重复展示(旧版本"人工处理"列统计的是 needs_manual_count,
            现按需求已换成退订口径,不再单独保留)。 */}
        <table>
          <thead>
            <tr>
              <th>账号 ID</th>
              <th>用户数</th>
              <th>Active</th>
              <th>Pending</th>
              <th>退订数</th>
            </tr>
          </thead>
          <tbody>
            {d.account_summaries.map((a) => (
              <tr key={a.account_id}>
                <td>{a.account_id}</td>
                <td>{a.total_users}</td>
                <td>{a.active_count}</td>
                <td>{a.pending_count}</td>
                <td>{a.canceled_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="section-title">到期预警(未来 7 天)</div>
        {d.expiry_warnings_7d.length === 0 ? (
          <p style={{ color: "#64748b", fontSize: 13 }}>暂无</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>账号</th>
                <th>邮箱</th>
                <th>层级</th>
                <th>到期日</th>
                <th>剩余天数</th>
              </tr>
            </thead>
            <tbody>
              {expiry.rows.map((w) => (
                <tr key={`${w.account_id}#${w.email}`}>
                  <td>{w.account_id}</td>
                  <td>{w.email}</td>
                  <td>{w.tier}</td>
                  <td>{w.subscription_end_at}</td>
                  <td>{w.days_remaining}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination
          page={expiry.safePage}
          totalPages={expiry.totalPages}
          total={d.expiry_warnings_7d.length}
          onPageChange={setExpiryPage}
        />
      </div>

      <div className="card">
        <div className="section-title">闲置用户(长期无Active记录)</div>
        {d.idle_users.length === 0 ? (
          <p style={{ color: "#64748b", fontSize: 13 }}>暂无</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>账号</th>
                <th>邮箱</th>
                <th>层级</th>
                <th>创建时间</th>
                <th>PENDING 天数</th>
              </tr>
            </thead>
            <tbody>
              {idle.rows.map((u) => (
                <tr key={`${u.account_id}#${u.email}`}>
                  <td>{u.account_id}</td>
                  <td>{u.email}</td>
                  <td>{u.tier}</td>
                  <td>{u.created_at ? formatBeijingTime(u.created_at) : "无数据"}</td>
                  <td>{u.days_pending ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination
          page={idle.safePage}
          totalPages={idle.totalPages}
          total={d.idle_users.length}
          onPageChange={setIdlePage}
        />
      </div>

      <div className="card">
        <div className="section-title">每日活跃用户数(近 30 天)</div>
        {/* v2.10:Active 指 subscription_status=ACTIVE。功能刚上线时历史快照
            数据不足 30 天,趋势图会只显示上线以后的天数,这是新指标从零开始
            积累历史数据的必然结果,不是缺陷,无法补出从未运行过的历史数据。 */}
        {dauTrend.loading && <p>加载中...</p>}
        {dauTrend.data && dauTrend.data.data_points.length === 0 && (
          <p style={{ color: "#64748b", fontSize: 13 }}>暂无历史快照数据(功能刚上线,需等待每日定时任务积累数据)</p>
        )}
        {dauTrend.data && dauTrend.data.data_points.length > 0 && (
          <>
            <table>
              <thead>
                <tr>
                  <th>日期</th>
                  <th>活跃用户数</th>
                </tr>
              </thead>
              <tbody>
                {/* v2.30:已按日期倒序(最近一天在最上面),见上方 dauPointsDesc */}
                {dau.rows.map((p) => (
                  <tr key={p.date}>
                    <td>{p.date}</td>
                    <td>{p.active_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={dau.safePage}
              totalPages={dau.totalPages}
              total={dauPointsDesc.length}
              onPageChange={setDauPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
